import React, { useContext, useEffect, useState } from 'react';
import { Nav } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FiMenu, FiX } from 'react-icons/fi';
import { menuAPI, permissionsAPI, authAPI } from '../api/api';
import { AuthContext } from '../context/AuthContext';
import './Sidebar.css';

function Sidebar() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [hierarchy, setHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [tablePermissions, setTablePermissions] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandDataManagement, setExpandDataManagement] = useState(false);
  const [expandApplicationMenus, setExpandApplicationMenus] = useState(true);

  useEffect(() => {
    if (user) {
      // Check if user is admin
      const userRole = user.Role || user.role || '';
      const isAdminUser = userRole.toLowerCase() === 'admin';
      setIsAdmin(isAdminUser);
      
      // Fetch permissions first, then fetch menu hierarchy with those permissions
      (async () => {
        try {
          const perms = await fetchTablePermissions();
          await fetchMenuHierarchy(perms);
        } catch (error) {

          setLoading(false);
        }
      })();
    }
  }, [user]);

  const fetchTablePermissions = async () => {
    try {
      const sessionRes = await authAPI.checkSession();
      if (sessionRes.data.success && sessionRes.data.user) {
        const userData = sessionRes.data.user;
        const userRole = userData.Role || userData.role || '';
        
        // Admin users have access to all tables
        if (userRole.toLowerCase() === 'admin') {
          setIsAdmin(true);
          return;
        }

        const groupIds = userData.groupIds || [userData.groupId];
        const groupId = groupIds[0];

        if (groupId) {
          const permRes = await permissionsAPI.getGroupPermissions(groupId);
          if (permRes.data.success && permRes.data.tablePermissions) {
            // Build a map of table names to their view permissions
            const perms = {};
            permRes.data.tablePermissions.forEach(tp => {
              perms[tp.TableName] = {
                CanView: tp.CanView === 1 || tp.CanView === true
              };
            });
            setTablePermissions(perms);
            return perms; // Return permissions so we can use them immediately
          }
        }
      }
    } catch (error) {

    }
    return {};
  };

  const fetchMenuHierarchy = async (perms = {}) => {
    try {
      const response = await menuAPI.getMenuHierarchy();
      if (response.data.success) {
        let hierarchy = response.data.hierarchy;
        
        // Get current user role to check if admin
        const userRole = user?.Role || user?.role || '';
        const isAdminUser = userRole.toLowerCase() === 'admin';
        
        // For non-admin client users, pre-fetch table names and filter items
        if (user && user.clientId && !isAdminUser) {
          hierarchy = await filterMenuByPermissions(hierarchy, perms);
        }
        
        // Show Configuration folder only to Administrator group
        const showConfig = isAdminUser || (user?.groupNames || user?.roles || []).some(g => /administrator/i.test(g));
        hierarchy = (hierarchy || []).filter(item => !item.isPagesFolder || showConfig);
        
        setHierarchy(hierarchy);
        // Keep folders collapsed by default
        setExpandedFolders(new Set());
      }
    } catch (error) {
      // Error fetching menu hierarchy
    } finally {
      setLoading(false);
    }
  };

  const filterMenuByPermissions = async (items, perms) => {
    const filtered = [];
    
    for (const item of items) {
      // Check if item should be shown
      if (item.Name === 'Manage Menu Items' || item.Name === 'Menus' || item.Name === 'MGB Setup') {
        continue; // Skip admin-only menus
      }
      // Configuration folder (formerly Pages) - admin only, skip for non-admins
      if (item.isPagesFolder) {
        continue;
      }

      // MGB_Menus items (pages) should always be shown if they have a link
      if (item.isMGBMenu && item.Link) {
        filtered.push(item);
        continue;
      }

      // If it's a folder, recursively process children first
      if (item.Folder) {
        if (item.children && item.children.length > 0) {
          item.children = await filterMenuByPermissions(item.children, perms);
          // Show folder if it has any visible children
          if (item.children.length > 0) {
            filtered.push(item);
          }
        }
        // Hide empty folders
      } else if (item.LinkRecordID && item.LinkRecordID !== -1) {
        // If item is linked to a table, check view permission
        try {
          const response = await menuAPI.getMenuItemTableName(item.MenuItemID);
          if (response.status === 200 && response.data.success) {
            const tableName = response.data.tableName;
            item.tableName = tableName;
            
            // Check if user has view permission using passed perms object
            if (perms[tableName] && perms[tableName].CanView) {
              filtered.push(item);
            }
            // Skip if no permission
          }
          // Skip if not found (404) or error
        } catch (error) {
          // Silently skip items that can't be resolved
        }
      } else {
        // Non-folder, non-table item (direct link items)
        filtered.push(item);
      }
    }
    
    return filtered;
  };

  const toggleFolder = (menuItemID) => {
    setExpandedFolders(prev => {
      const updated = new Set(prev);
      if (updated.has(menuItemID)) {
        updated.delete(menuItemID);
      } else {
        updated.add(menuItemID);
      }
      return updated;
    });
  };

  const canViewTable = async (menuItem) => {
    // Admin users can view all tables
    if (isAdmin) return true;

    // Client users need to have explicit view permission
    if (menuItem.LinkRecordID && menuItem.LinkRecordID !== -1) {
      try {
        const response = await menuAPI.getMenuItemTableName(menuItem.MenuItemID);
        if (response.data.success) {
          const tableName = response.data.tableName;
          // Check if we have this in permissions
          if (tablePermissions[tableName]) {
            return tablePermissions[tableName].CanView === true;
          }
          // If not in permissions and user is not admin, deny access
          return false;
        }
      } catch (error) {

        return false;
      }
    }
    return true;
  };

  const shouldShowItem = (item) => {
    // Hide admin-only menus for client users
    if ((item.Name === 'Manage Menu Items' || item.Name === 'Menus' || item.Name === 'MGB Setup') && user && user.clientId) {
      return false;
    }

    // For non-admin client users, filter table menu items by permission
    if (user && user.clientId && !isAdmin) {
      if (item.LinkRecordID && item.LinkRecordID !== -1) {
        // This is a table link - check permissions synchronously if available
        const tableName = item.tableName; // This would need to be pre-fetched
        if (tableName && tablePermissions[tableName]) {
          return tablePermissions[tableName].CanView === true;
        }
        // If we don't have the table name yet, hide it to be safe
        // (Will be shown after permissions are fetched)
        return false;
      }
    }

    return true;
  };

  const handleMenuItemClick = async (item) => {
    try {
      // If item is from MGB_Menus (has isMGBMenu flag), handle external link
      if (item.isMGBMenu && item.Link) {
        // Check if it's an external URL (starts with http:// or https://)
        if (item.Link.startsWith('http://') || item.Link.startsWith('https://')) {
          window.open(item.Link, '_blank');
        } else {
          // Internal link
          navigate(item.Link);
        }
        return;
      }

      // If item has a direct link, navigate to it
      if (item.Link) {
        navigate(item.Link);
        return;
      }

      // If item is linked to a table, get the table name and navigate
      if (item.LinkRecordID && item.LinkRecordID !== -1) {
        const response = await menuAPI.getMenuItemTableName(item.MenuItemID);
        
        if (response.data.success) {
          const tableName = response.data.tableName;
          
          // Check if user has permission to view this table
          if (!isAdmin && user && user.clientId) {
            if (!tablePermissions[tableName] || !tablePermissions[tableName].CanView) {
              alert('You do not have permission to view this table');
              return;
            }
          }
          
          navigate(`/tables/${tableName}`);
        }
      }
    } catch (error) {
      // Error handling menu item click
    }
  };

  const renderMenuItems = (items, level = 0) => {
    if (!items || items.length === 0) return null;

    return items.map(item => {
      const isFolder = item.Folder === 1 || item.Folder === true;
      const isExpanded = expandedFolders.has(item.MenuItemID);
      const hasChildren = isFolder && item.children && item.children.length > 0;
      const indent = level * 20;

      return (
        <div key={item.MenuItemID}>
          <div
            style={{
              paddingLeft: `${indent}px`,
              display: 'flex',
              alignItems: 'center',
              padding: '8px 8px 8px calc(8px + ' + indent + 'px)',
              borderLeft: level > 0 ? '1px solid #ddd' : 'none',
              marginLeft: level > 0 ? '8px' : '0'
            }}
          >
            {isFolder && hasChildren ? (
              <button
                onClick={() => toggleFolder(item.MenuItemID)}
                className="menu-expand-btn"
              >
                {isExpanded ? '▼' : '▶'}
              </button>
            ) : (
              <span className="menu-expand-btn-placeholder"></span>
            )}

            <span className="menu-icon" style={{ marginRight: '8px' }}>
              {isFolder ? '📁' : '📄'}
            </span>

            {isFolder ? (
              <div className="menu-folder-title">{item.Name}</div>
            ) : (
              <button
                onClick={() => handleMenuItemClick(item)}
                className="menu-link-button"
                title={item.Name}
              >
                {item.Name}
              </button>
            )}
          </div>

          {isFolder && isExpanded && hasChildren && (
            <div className="menu-children">
              {renderMenuItems(item.children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  if (loading) {
    return <div className="sidebar">Loading menus...</div>;
  }

  // Check if user is admin (case-insensitive)
  const userRole = user?.Role || user?.role || '';
  const isAdminUser = userRole.toLowerCase() === 'admin';

  // For non-admin client users, only show APPLICATION MENUS
  if (user && user.clientId && !isAdminUser) {
    return (
      <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
        <button 
          className="sidebar-toggle"
          onClick={() => setIsCollapsed(!isCollapsed)}
          title={isCollapsed ? 'Show sidebar' : 'Hide sidebar'}
        >
          {isCollapsed ? <FiMenu size={20} /> : <FiX size={20} />}
        </button>
        {!isCollapsed && (
          <div>
            <div style={{ fontSize: '11px', padding: '8px 10px', color: '#666', fontWeight: 'bold' }}>
              APPLICATION MENUS:
            </div>
            <div className="menu-hierarchy">
              {renderMenuItems(hierarchy)}
            </div>
          </div>
        )}
      </div>
    );
  }

  // For admin users and non-client users, show full menu
  return (
    <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      <button 
        className="sidebar-toggle"
        onClick={() => setIsCollapsed(!isCollapsed)}
        title={isCollapsed ? 'Show sidebar' : 'Hide sidebar'}
      >
        {isCollapsed ? <FiMenu size={20} /> : <FiX size={20} />}
      </button>
      {!isCollapsed && (
        <Nav className="flex-column">
          <Nav.Link onClick={() => navigate('/dashboard')} className="menu-item" style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
            <span style={{ fontSize: '14px' }}>📊</span>
            <span>Dashboard</span>
          </Nav.Link>
          
          {/* Data Management Group - Collapsible */}
          <button
            onClick={() => setExpandDataManagement(!expandDataManagement)}
            style={{
              background: 'none',
              border: 'none',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 20px',
              marginTop: '6px',
              color: '#555',
              fontWeight: '600',
              fontSize: '13px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.color = '#28a745'}
            onMouseLeave={(e) => e.target.style.color = '#555'}
          >
            <span style={{ minWidth: '14px', fontSize: '11px' }}>{expandDataManagement ? '▼' : '▶'}</span>
            <span style={{ fontSize: '12px' }}>📦</span>
            <span>Data Management</span>
          </button>
          
          {expandDataManagement && (
            <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '0px' }}>
              <Nav.Link onClick={() => navigate('/tables')} className="menu-item" style={{ paddingLeft: '35px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                <span style={{ fontSize: '11px' }}>📊</span>
                <span>Tables</span>
              </Nav.Link>
              <Nav.Link onClick={() => navigate('/relationships')} className="menu-item" style={{ paddingLeft: '35px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                <span style={{ fontSize: '11px' }}>🔗</span>
                <span>Relationships</span>
              </Nav.Link>
              <Nav.Link onClick={() => navigate('/mgb-setup')} className="menu-item" style={{ paddingLeft: '35px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                <span style={{ fontSize: '11px' }}>⚙️</span>
                <span>MGB Setup</span>
              </Nav.Link>
              {(isAdmin || !user.clientId) && (
              <Nav.Link onClick={() => navigate('/menu-management')} className="menu-item" style={{ paddingLeft: '35px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                <span style={{ fontSize: '11px' }}>📋</span>
                <span>Manage Items</span>
              </Nav.Link>
              )}
            </div>
          )}
          
          {isAdmin && (
            <>
              <hr style={{ margin: '6px 0', borderColor: '#ddd' }} />
              <Nav.Link onClick={() => navigate('/admin')} className="menu-item admin-item" style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                <span style={{ fontSize: '14px' }}>⚙️</span>
                <span>Admin Panel</span>
              </Nav.Link>
            </>
          )}
          
          <hr style={{ margin: '6px 0', borderColor: '#ddd' }} />
          
          {/* Application Menus - Collapsible */}
          <button
            onClick={() => setExpandApplicationMenus(!expandApplicationMenus)}
            style={{
              background: 'none',
              border: 'none',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 20px',
              marginTop: '0px',
              color: '#555',
              fontWeight: '600',
              fontSize: '13px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.color = '#28a745'}
            onMouseLeave={(e) => e.target.style.color = '#555'}
          >
            <span style={{ minWidth: '14px', fontSize: '11px' }}>{expandApplicationMenus ? '▼' : '▶'}</span>
            <span style={{ fontSize: '12px' }}>📂</span>
            <span>App Menus</span>
          </button>
          
          {expandApplicationMenus && (
            <div className="menu-hierarchy" style={{ marginTop: '2px' }}>
              {renderMenuItems(hierarchy)}
            </div>
          )}
        </Nav>
      )}
    </div>
  );
}

export default Sidebar;
